import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient, errorMessage, unwrap } from '../lib/api'
import { useJobStream, JOB_STAGE_LABEL } from '../hooks/useJobStream'
import { queryKeys, useChannels, useEstimate, usePersonas, useSettings } from '../lib/queries'
import { ErrorBox, InlineError } from '../components/QueryState'
import { platformLabel } from '../lib/platform'
import { instagramSettingsProblem } from '../lib/instagramReadiness'
import { trackOnce } from '../../../family-design/analytics'

/** ラジオ・チェックボックスを視覚的に隠したラベル型の選択肢（キーボード操作とフォーカス表示を残す） */
const choiceLabelCls = (active: boolean) =>
  `cursor-pointer rounded-ctl border text-sm focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 ${
    active ? 'border-primary bg-primary-subtle font-bold text-primary' : 'border-line-strong bg-surface hover:bg-surface-2'
  }`

/** 入力欄の見出し（DESIGN-SYSTEM.md の gm-field と同じ 13px / 700） */
const fieldLabelCls = 'text-[13px] font-bold'

export function Compose() {
  const nav = useNavigate()
  const qc = useQueryClient()
  // 失敗した投稿の「作り直す」から来た場合は同じお題・種別・ペルソナで始める
  const [params] = useSearchParams()
  const [personaId, setPersonaId] = useState(() => params.get('personaId') ?? '')
  const [kind, setKind] = useState<'tips' | 'product'>(() => (params.get('kind') === 'product' ? 'product' : 'tips'))
  const [topic, setTopic] = useState(() => params.get('topic') ?? '')
  const [points, setPoints] = useState('')
  const [channelIds, setChannelIds] = useState<string[]>([])
  const [error, setError] = useState('')
  const [jobId, setJobId] = useState<string | null>(null)
  const job = useJobStream(jobId)

  // 生成完了で承認キューへ（audit M-6: 従来は即遷移して「承認待ちはありません」が出ていた）
  useEffect(() => {
    if (job?.status === 'completed') {
      trackOnce('generate:' + jobId, 'generate', { content_type: 'post' })
      qc.invalidateQueries({ queryKey: queryKeys.queue })
      qc.invalidateQueries({ queryKey: queryKeys.credits })
      nav('/queue')
    }
  }, [job?.status, jobId, qc, nav])

  const personas = usePersonas()
  const channels = useChannels(personaId)
  const settings = useSettings()
  const imageCount = settings.data?.image.enabled ? 1 : 0
  const estimate = useEstimate(kind, imageCount)

  const generate = useMutation({
    mutationFn: async () => unwrap(await apiClient.post<{ postId: string; jobId: string }>('/posts/generate', {
      personaId, kind, channelIds,
      input: { topic, points: points.split('\n').map(s => s.trim()).filter(Boolean) },
    })),
    onSuccess: (data) => setJobId(data.jobId),   // 即遷移せず SSE で進捗を追う
    onError: (e) => setError(errorMessage(e)),
  })

  // 進捗が取れなくなった（lost）・失敗・キャンセルはスピナーを止める
  const jobActive = !!job && !job.lost && job.status !== 'failed' && job.status !== 'canceled'
  const generating = generate.isPending || jobActive

  const activeChannels = (channels.data ?? []).filter(c => c.status === 'active')
  // Instagram を選んでいて、画像の設定が Instagram に合わないときは、押す前に知らせて生成させない
  const instagramSelected = activeChannels.some(c => c.platform === 'instagram' && channelIds.includes(c.id))
  const instagramProblem = instagramSelected ? instagramSettingsProblem(settings.data) : null

  return (
    <div className="max-w-3xl space-y-4">
      <h1 className="text-2xl font-bold leading-snug">投稿を作る</h1>

      {personas.isError && (
        <ErrorBox message={`ペルソナの読み込みに失敗しました: ${errorMessage(personas.error)}`}
          onRetry={() => { void personas.refetch() }} />
      )}
      <div className="gm-card grid gap-4 p-4 md:p-5">
        <label className="grid gap-1.5">
          <span className={fieldLabelCls}>ペルソナ</span>
          <select value={personaId} onChange={e => { setPersonaId(e.target.value); setChannelIds([]) }}
            className="gm-input">
            <option value="">{personas.isPending ? '読み込み中…' : '選択してください'}</option>
            {personas.data?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>

        <fieldset className="flex flex-wrap gap-3">
          <legend className="sr-only">投稿の種類</legend>
          {(['tips', 'product'] as const).map(k => (
            <label key={k} className={`px-3 py-2 ${choiceLabelCls(kind === k)}`}>
              <input type="radio" name="compose-kind" className="sr-only" checked={kind === k} onChange={() => setKind(k)} />
              {k === 'tips' ? 'お役立ち投稿' : '商品紹介'}
            </label>
          ))}
        </fieldset>

        <label className="grid gap-1.5">
          <span className={fieldLabelCls}>お題（例: タオルの長持ちする洗い方）</span>
          <input value={topic} onChange={e => setTopic(e.target.value)}
            className="gm-input" />
        </label>

        <label className="grid gap-1.5">
          <span className={fieldLabelCls}>盛り込みたい要点（1行に1つ・任意）</span>
          <textarea value={points} onChange={e => setPoints(e.target.value)} rows={3}
            className="gm-input h-auto py-2" />
        </label>

        {personaId && (
          <fieldset className="text-sm">
            <legend className={fieldLabelCls}>投稿先チャンネル</legend>
            {channels.isError && (
              <ErrorBox message={`チャンネルの読み込みに失敗しました: ${errorMessage(channels.error)}`}
                onRetry={() => { void channels.refetch() }} />
            )}
            {channels.isPending && <p className="mt-1.5 text-ink-muted">読み込み中…</p>}
            {channels.isSuccess && activeChannels.length === 0 && (
              <p className="mt-1.5 text-ink-muted">
                接続済みチャンネルがありません（<Link to="/personas" className="font-bold text-primary underline">ペルソナ</Link>ページで接続）
              </p>
            )}
            <div className="mt-1.5 flex flex-wrap gap-2">
              {activeChannels.map(c => (
                <label key={c.id} className={`px-3 py-1.5 ${choiceLabelCls(channelIds.includes(c.id))}`}>
                  <input type="checkbox" className="sr-only"
                    checked={channelIds.includes(c.id)}
                    onChange={e => setChannelIds(ids => e.target.checked ? [...ids, c.id] : ids.filter(x => x !== c.id))} />
                  {platformLabel(c.platform)} {c.handle}
                </label>
              ))}
            </div>
            {instagramProblem && (
              <p role="alert" className="mt-2 text-sm text-warn">
                {instagramProblem}<Link to="/settings" className="ml-1 font-bold text-primary underline">設定を開く</Link>
              </p>
            )}
          </fieldset>
        )}
      </div>

      <InlineError message={error} />

      {/* 生成進捗（SSE）。失敗時はエラー＋再試行、キャンセル・進捗不明はその旨を表示 */}
      {job && job.status !== 'completed' && (
        <div role="status" className="gm-card p-3 text-sm">
          {job.lost ? (
            <p className="text-warn">
              進捗を取得できなくなりました（{job.error ?? '通信エラー'}）。生成は続いている可能性があります。
              <Link to="/queue" className="ml-1 font-bold text-primary underline">承認キュー</Link>で状況を確認してください。
            </p>
          ) : job.status === 'failed' ? (
            <div className="space-y-2">
              <p className="text-bad">生成に失敗しました{job.error ? `: ${job.error}` : ''}</p>
              <button disabled={!!instagramProblem} onClick={() => { setJobId(null); setError(''); generate.mutate() }}
                className="gm-btn gm-btn-secondary">
                もう一度生成する
              </button>
            </div>
          ) : job.status === 'canceled' ? (
            <p className="text-ink-muted">キャンセルされました</p>
          ) : (
            /* AI が生成中（グラデの輪は生成ジョブの進行中だけに使う） */
            <p className="flex items-center gap-2">
              <span className="gm-ai-ring" aria-hidden />
              {JOB_STAGE_LABEL[job.progress?.stage ?? ''] ?? '生成を開始しています…'}
            </p>
          )}
        </div>
      )}

      <button
        disabled={!personaId || !topic.trim() || channelIds.length === 0 || generating || !!instagramProblem}
        onClick={() => { setError(''); setJobId(null); generate.mutate() }}
        className="gm-btn gm-btn-primary">
        {/* 見積もりは丸めない（残高表示の 5 単位繰上げはしない） */}
        {generating ? '生成中…' : <>生成する（約 <span className="gm-num">{estimate.data?.credits ?? '…'}</span> cr）</>}
      </button>
      {estimate.isError && <p className="text-xs text-warn">消費クレジットの見積もりを取得できませんでした</p>}
      <p className="text-xs text-ink-muted">生成が終わると承認キューに入ります。承認するまで投稿されません。</p>
    </div>
  )
}
